import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Form } from "formik";
import SubmitButton from "../../components/Forms/SubmitButton";
import SettingsSelect from "../../components/Forms/SettingsSelect";
import DatePicker from "react-datepicker";
import moment from "moment";

export default function ReportChatAudit() {
    const [loading, setLoading] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [properties, setProperties] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({ bookings: [], title: "" });
    const [loadingModal, setLoadingModal] = useState(false);
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [staffModalData, setStaffModalData] = useState({ messages: [], title: "" });
    const [loadingStaffModal, setLoadingStaffModal] = useState(false);
    const [currentModalParams, setCurrentModalParams] = useState(null);
    const [modalSearchTerm, setModalSearchTerm] = useState("");
    const [includeBulkMessages, setIncludeBulkMessages] = useState(false);
    const [showCallsModal, setShowCallsModal] = useState(false);
    const [callsModalData, setCallsModalData] = useState({ calls: [], title: "" });
    const [loadingCallsModal, setLoadingCallsModal] = useState(false);

    useEffect(() => {
        // Fetch properties when component mounts
        const fetchProperties = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}properties`);
                // The API returns properties with value/label format already
                setProperties([{ value: 0, label: "All Properties" }, ...response.data]);
            } catch (error) {
                console.error("Error fetching properties:", error);
            }
        };

        fetchProperties();
    }, []);

    const handleCellClick = async (date, source, propertyId) => {
        const sourceLabels = {
            whatsapp: "WhatsApp",
            web_form: "Web Form",
            email: "Email",
        };

        setModalData({
            bookings: [],
            title: `${sourceLabels[source]} Chats - ${moment(date).format("DD MMM YYYY")}`,
        });
        setShowModal(true);
        setLoadingModal(true);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}reports/new_chats_detail`, {
                params: {
                    date: date,
                    source: source,
                    property_id: propertyId || 0,
                },
            });

            setModalData({
                bookings: response.data,
                title: `${sourceLabels[source]} Chats - ${moment(date).format("DD MMM YYYY")} (${response.data.length})`,
            });
        } catch (error) {
            console.error("Error fetching booking details:", error);
            setModalData({
                bookings: [],
                title: `Error loading data`,
            });
        } finally {
            setLoadingModal(false);
        }
    };

    const handleStaffMessageClick = async (userId, userName, date, reportType, propertyId, includeBulk, platform = null, reportFromDate = null, reportToDate = null) => {
        const dateDisplay = reportType === "day" ? moment(date).format("DD MMM YYYY") : moment(date, "YYYY-MM").format("MMMM YYYY");
        const platformLabels = { whatsapp: "WhatsApp", email: "Email", note: "Notes" };
        const platformLabel = platform ? ` - ${platformLabels[platform] || platform}` : "";

        // Store parameters for download
        setCurrentModalParams({ userId, userName, date, reportType, propertyId, includeBulk, platform });

        setStaffModalData({
            messages: [],
            title: `${userName} - ${dateDisplay}${platformLabel}`,
        });
        setShowStaffModal(true);
        setLoadingStaffModal(true);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}reports/staff_messages_detail`, {
                params: {
                    user_id: userId,
                    date: date,
                    report_type: reportType,
                    property_id: propertyId || 0,
                    include_bulk_messages: includeBulk || false,
                    platform: platform || undefined,
                    from_date: reportFromDate || undefined,
                    to_date: reportToDate || undefined,
                },
            });

            setStaffModalData({
                messages: response.data,
                title: `${userName} - ${dateDisplay}${platformLabel} (${response.data.length} messages)`,
            });
        } catch (error) {
            console.error("Error fetching message details:", error);
            setStaffModalData({
                messages: [],
                title: `Error loading data`,
            });
        } finally {
            setLoadingStaffModal(false);
        }
    };

    const handleCallsClick = async (userId, userName, date, reportType, propertyId, reportFromDate = null, reportToDate = null) => {
        const dateDisplay = reportType === "day" ? moment(date).format("DD MMM YYYY") : moment(date, "YYYY-MM").format("MMMM YYYY");

        setCallsModalData({
            calls: [],
            title: `${userName} - Calls - ${dateDisplay}`,
        });
        setShowCallsModal(true);
        setLoadingCallsModal(true);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}reports/call_logs_detail`, {
                params: {
                    user_id: userId,
                    date: date,
                    report_type: reportType,
                    property_id: propertyId || 0,
                    from_date: reportFromDate || undefined,
                    to_date: reportToDate || undefined,
                },
            });

            setCallsModalData({
                calls: response.data,
                title: `${userName} - Calls - ${dateDisplay} (${response.data.length} calls)`,
            });
        } catch (error) {
            console.error("Error fetching call details:", error);
            setCallsModalData({
                calls: [],
                title: `Error loading data`,
            });
        } finally {
            setLoadingCallsModal(false);
        }
    };

    const handleDownloadStaffMessages = async () => {
        if (!currentModalParams) return;

        const { userId, userName, date, reportType, propertyId } = currentModalParams;

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_API_URL}reports/export_staff_messages_detail`,
                responseType: "blob",
                data: {
                    user_id: userId,
                    user_name: userName,
                    date: date,
                    report_type: reportType,
                    property_id: propertyId || 0,
                },
            });

            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", response.headers["content-disposition"]?.split("filename=")[1] || "Staff_Messages.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.error("Error downloading staff messages:", error);
            alert("Error downloading report");
        }
    };

    const renderRegularTable = (values, data) => {
        // Early return if no data
        if (!data || !Array.isArray(data) || data.length === 0) {
            return <p>No data available</p>;
        }

        // Special handling for new_chats_day with source split
        if (values.audit_type === "new_chats_day") {
            // Group data by date and source
            const groupedData = data.reduce((acc, row) => {
                if (!acc[row.dt]) {
                    acc[row.dt] = { whatsapp: 0, web_form: 0, email: 0 };
                }
                if (row.source === "whatsapp") {
                    acc[row.dt].whatsapp = parseInt(row.total_chats);
                } else if (row.source === "web_form") {
                    acc[row.dt].web_form = parseInt(row.total_chats);
                } else if (row.source === "email") {
                    acc[row.dt].email = parseInt(row.total_chats);
                }
                return acc;
            }, {});

            const sortedDates = Object.keys(groupedData).sort();

            return (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="text-left px-4 py-2 border">Date</th>
                                <th className="text-left px-4 py-2 border bg-green-50">WhatsApp</th>
                                <th className="text-left px-4 py-2 border bg-blue-50">Web Form</th>
                                <th className="text-left px-4 py-2 border bg-purple-50">Email</th>
                                <th className="text-left px-4 py-2 border bg-gray-100">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedDates.map((date) => {
                                const whatsapp = groupedData[date].whatsapp;
                                const webForm = groupedData[date].web_form;
                                const email = groupedData[date].email;
                                const total = whatsapp + webForm + email;
                                return (
                                    <tr key={date} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border">{moment(date).format("DD MMM YYYY")}</td>
                                        <td
                                            className="px-4 py-2 border text-center bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
                                            onClick={() => whatsapp > 0 && handleCellClick(date, "whatsapp", values.property_id)}
                                        >
                                            {whatsapp > 0 ? <span className="underline text-blue-600">{whatsapp}</span> : whatsapp}
                                        </td>
                                        <td
                                            className="px-4 py-2 border text-center bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors"
                                            onClick={() => webForm > 0 && handleCellClick(date, "web_form", values.property_id)}
                                        >
                                            {webForm > 0 ? <span className="underline text-blue-600">{webForm}</span> : webForm}
                                        </td>
                                        <td
                                            className="px-4 py-2 border text-center bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors"
                                            onClick={() => email > 0 && handleCellClick(date, "email", values.property_id)}
                                        >
                                            {email > 0 ? <span className="underline text-blue-600">{email}</span> : email}
                                        </td>
                                        <td className="px-4 py-2 border text-center font-medium bg-gray-100">{total}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-gray-200 font-bold">
                                <td className="px-4 py-2 border">Total</td>
                                <td className="px-4 py-2 border text-center bg-green-100">{sortedDates.reduce((sum, date) => sum + groupedData[date].whatsapp, 0)}</td>
                                <td className="px-4 py-2 border text-center bg-blue-100">{sortedDates.reduce((sum, date) => sum + groupedData[date].web_form, 0)}</td>
                                <td className="px-4 py-2 border text-center bg-purple-100">{sortedDates.reduce((sum, date) => sum + groupedData[date].email, 0)}</td>
                                <td className="px-4 py-2 border text-center bg-gray-200">
                                    {sortedDates.reduce((sum, date) => sum + groupedData[date].whatsapp + groupedData[date].web_form + groupedData[date].email, 0)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm space-y-1">
                        <p className="text-green-700">
                            * <strong>WhatsApp</strong>: Users who sent their first incoming WhatsApp message
                        </p>
                        <p className="text-blue-700">
                            * <strong>Web Form</strong>: Users created via frontend web form submission
                        </p>
                        <p className="text-purple-700">
                            * <strong>Email</strong>: Users created via email processing (Property24, private email, etc.)
                        </p>
                    </div>

                    {/* Modal for showing bookings */}
                    {showModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
                            <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold">{modalData.title}</h3>
                                    <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
                                        ×
                                    </button>
                                </div>

                                {loadingModal ? (
                                    <div className="text-center py-8">Loading...</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="text-left px-4 py-2 border">Booking Ref</th>
                                                    <th className="text-left px-4 py-2 border">Year</th>
                                                    <th className="text-left px-4 py-2 border">Name</th>
                                                    <th className="text-left px-4 py-2 border">Email</th>
                                                    <th className="text-left px-4 py-2 border">Cell</th>
                                                    <th className="text-left px-4 py-2 border">First Message</th>
                                                    <th className="text-left px-4 py-2 border">Created</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {modalData.bookings.length > 0 ? (
                                                    modalData.bookings.map((booking, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-4 py-2 border">
                                                                <a
                                                                    href={`/booking/all?user_id=${booking.user_id}&booking_id=${booking.booking_id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 hover:underline"
                                                                >
                                                                    {booking.booking_reference}
                                                                </a>
                                                            </td>
                                                            <td className="px-4 py-2 border text-center">{booking.year || "-"}</td>
                                                            <td className="px-4 py-2 border">
                                                                {booking.name} {booking.surname}
                                                            </td>
                                                            <td className="px-4 py-2 border">{booking.email || "-"}</td>
                                                            <td className="px-4 py-2 border">{booking.cell || "-"}</td>
                                                            <td className="px-4 py-2 border text-sm max-w-xs truncate" title={booking.first_message}>
                                                                {booking.first_message || "-"}
                                                            </td>
                                                            <td className="px-4 py-2 border">{moment(booking.created_at).format("DD MMM YYYY HH:mm")}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="px-4 py-4 text-center text-gray-500">
                                                            No bookings found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Regular table for other report types
        const isChatsSent = values.audit_type === "chats_sent_month" || values.audit_type === "chats_sent_day";
        const isMonthReport = values.audit_type === "chats_sent_month";
        const dateInputFormat = isMonthReport ? "YYYY-MM" : "YYYY-MM-DD";
        const dateDisplayFormat = isMonthReport ? "MMMM YYYY" : "DD MMM YYYY";
        const reportType = isMonthReport ? "month" : "day";

        const formattedFromDate = fromDate ? moment(fromDate).format("YYYY-MM-DD") : null;
        const formattedToDate = toDate ? moment(toDate).format("YYYY-MM-DD") : null;

        const renderClickableCount = (count, userId, userName, dt, platform = null) => {
            if (!count || parseInt(count) === 0) return <span className="text-gray-400">0</span>;
            return (
                <span
                    className="underline text-blue-600 cursor-pointer"
                    onClick={() => userId && handleStaffMessageClick(userId, userName, dt, reportType, values.property_id, values.include_bulk_messages, platform, formattedFromDate, formattedToDate)}
                >
                    {count}
                </span>
            );
        };

        const renderClickableCallCount = (count, userId, userName, dt) => {
            if (!count || parseInt(count) === 0) return <span className="text-gray-400">0</span>;
            return (
                <span
                    className="underline text-blue-600 cursor-pointer"
                    onClick={() => userId && handleCallsClick(userId, userName, dt, reportType, values.property_id, formattedFromDate, formattedToDate)}
                >
                    {count}
                </span>
            );
        };

        // Chats Sent reports: show per-platform columns
        if (isChatsSent) {
            return (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="text-left px-4 py-2 border">Date</th>
                                <th className="text-left px-4 py-2 border">User</th>
                                <th className="text-center px-4 py-2 border bg-green-50">WhatsApp</th>
                                <th className="text-center px-4 py-2 border bg-purple-50">Email</th>
                                <th className="text-center px-4 py-2 border bg-gray-100">Total</th>
                                <th className="text-center px-4 py-2 border bg-yellow-50">Notes</th>
                                <th className="text-center px-4 py-2 border bg-blue-50">Calls Made</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((row, index) => {
                                const userName = `${row.name} ${row.surname}`;
                                const whatsapp = parseInt(row.whatsapp_count || 0);
                                const email = parseInt(row.email_count || 0);
                                const note = parseInt(row.note_count || 0);
                                const calls = parseInt(row.call_count || 0);
                                const total = whatsapp + email;
                                return (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border">{moment(row.dt, dateInputFormat).format(dateDisplayFormat)}</td>
                                        <td className="px-4 py-2 border">{userName}</td>
                                        <td className="px-4 py-2 border text-center bg-green-50 hover:bg-green-100 transition-colors">
                                            {renderClickableCount(whatsapp, row.user_id, userName, row.dt, "whatsapp")}
                                        </td>
                                        <td className="px-4 py-2 border text-center bg-purple-50 hover:bg-purple-100 transition-colors">
                                            {renderClickableCount(email, row.user_id, userName, row.dt, "email")}
                                        </td>
                                        <td className="px-4 py-2 border text-center font-medium bg-gray-100 hover:bg-gray-200 transition-colors">
                                            {renderClickableCount(total, row.user_id, userName, row.dt, null)}
                                        </td>
                                        <td className="px-4 py-2 border text-center bg-yellow-50 hover:bg-yellow-100 transition-colors">
                                            {renderClickableCount(note, row.user_id, userName, row.dt, "note")}
                                        </td>
                                        <td className="px-4 py-2 border text-center bg-blue-50 hover:bg-blue-100 transition-colors">
                                            {renderClickableCallCount(calls, row.user_id, userName, row.dt)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-gray-200 font-bold">
                                <td className="px-4 py-2 border" colSpan="2">
                                    Total
                                </td>
                                <td className="px-4 py-2 border text-center bg-green-100">{data.reduce((sum, row) => sum + parseInt(row.whatsapp_count || 0), 0)}</td>
                                <td className="px-4 py-2 border text-center bg-purple-100">{data.reduce((sum, row) => sum + parseInt(row.email_count || 0), 0)}</td>
                                <td className="px-4 py-2 border text-center bg-gray-200">{data.reduce((sum, row) => sum + parseInt(row.whatsapp_count || 0) + parseInt(row.email_count || 0), 0)}</td>
                                <td className="px-4 py-2 border text-center bg-yellow-100">{data.reduce((sum, row) => sum + parseInt(row.note_count || 0), 0)}</td>
                                <td className="px-4 py-2 border text-center bg-blue-100">{data.reduce((sum, row) => sum + parseInt(row.call_count || 0), 0)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            );
        }

        // Bookings Updated and other report types: simple table
        return (
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="text-left px-4 py-2 border">Date</th>
                            <th className="text-left px-4 py-2 border">User</th>
                            <th className="text-left px-4 py-2 border">Total Changes</th>
                            <th className="text-left px-4 py-2 border">Unique Bookings</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-2 border">{moment(row.dt, "YYYY-MM").format("MMMM YYYY")}</td>
                                <td className="px-4 py-2 border">{row.name} {row.surname}</td>
                                <td className="px-4 py-2 border text-center">{row.total_changes}</td>
                                <td className="px-4 py-2 border text-center">{row.unique_bookings}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-gray-200 font-bold">
                            <td className="px-4 py-2 border" colSpan="2">Total</td>
                            <td className="px-4 py-2 border text-center">{data.reduce((sum, row) => sum + parseInt(row.total_changes || 0), 0)}</td>
                            <td className="px-4 py-2 border text-center">{data.reduce((sum, row) => sum + parseInt(row.unique_bookings || 0), 0)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };

    const renderFolderTable = (data) => {
        const metadata = data._metadata || null;

        // Get all unique dates from all folders
        const allDates = new Set();
        const folders = [];

        // Extract folder names and all dates
        Object.entries(data).forEach(([key, value]) => {
            if (typeof value === "object" && !["unique_bookings", "_metadata", "orphaned_logs", "deleted_bookings"].includes(key)) {
                folders.push(key);
                Object.keys(value).forEach((date) => allDates.add(date));
            }
        });

        // Sort dates
        const sortedDates = Array.from(allDates).sort();

        return (
            <div>
                {/* Metadata section */}
                {metadata && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-medium mb-2">Report Summary</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p>
                                    Period: {metadata.date_range.start} to {metadata.date_range.end}
                                </p>
                                <p>Total Unique Bookings: {metadata.total_unique_bookings}</p>
                            </div>
                            <div>
                                <p className="text-amber-600">
                                    Orphaned Logs: {metadata.total_orphaned_logs}({metadata.total_unique_orphaned_bookings} unique)
                                </p>
                                <p className="text-red-600">
                                    Deleted Bookings: {metadata.total_deleted_logs}({metadata.total_unique_deleted_bookings} unique)
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main table */}
                <div className="overflow-x-auto">
                    <table className="w-full min-w-max">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="text-left px-4 py-2 border">Date</th>
                                {folders.map((folder) => (
                                    <th key={folder} className="text-left px-4 py-2 border">
                                        {folder}
                                    </th>
                                ))}
                                <th className="text-left px-4 py-2 border bg-gray-100">Folder Total</th>
                                <th className="text-left px-4 py-2 border bg-blue-50">Unique Bookings</th>
                                <th className="text-left px-4 py-2 border bg-amber-50">Orphaned</th>
                                <th className="text-left px-4 py-2 border bg-red-50">Deleted</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedDates.map((date) => {
                                const folderTotal = folders.reduce((total, folder) => {
                                    return total + (data[folder]?.[date] || 0);
                                }, 0);

                                return (
                                    <tr key={date} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 border font-medium">{moment(date).format("DD MMM YYYY")}</td>
                                        {folders.map((folder) => (
                                            <td key={`${date}-${folder}`} className="px-4 py-2 border text-center">
                                                {data[folder]?.[date] || 0}
                                            </td>
                                        ))}
                                        <td className="px-4 py-2 border text-center font-medium bg-gray-50">{folderTotal}</td>
                                        <td className="px-4 py-2 border text-center font-medium bg-blue-50">{data.unique_bookings?.[date] || 0}</td>
                                        <td className="px-4 py-2 border text-center font-medium bg-amber-50">
                                            <div>
                                                {data.orphaned_logs?.[date]?.total_count || 0}
                                                {data.orphaned_logs?.[date]?.unique_count > 0 && <div className="text-xs text-amber-600">({data.orphaned_logs[date].unique_count} unique)</div>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 border text-center font-medium bg-red-50">
                                            <div>
                                                {data.deleted_bookings?.[date]?.total_count || 0}
                                                {data.deleted_bookings?.[date]?.unique_count > 0 && <div className="text-xs text-red-600">({data.deleted_bookings[date].unique_count} unique)</div>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-gray-200 font-bold">
                                <td className="px-4 py-2 border">Total</td>
                                {folders.map((folder) => (
                                    <td key={`total-${folder}`} className="px-4 py-2 border text-center">
                                        {sortedDates.reduce((sum, date) => sum + (data[folder]?.[date] || 0), 0)}
                                    </td>
                                ))}
                                <td className="px-4 py-2 border text-center bg-gray-200">
                                    {sortedDates.reduce((sum, date) => {
                                        return sum + folders.reduce((folderSum, folder) => folderSum + (data[folder]?.[date] || 0), 0);
                                    }, 0)}
                                </td>
                                <td className="px-4 py-2 border text-center bg-blue-100">{sortedDates.reduce((sum, date) => sum + (data.unique_bookings?.[date] || 0), 0)}</td>
                                <td className="px-4 py-2 border text-center bg-amber-100">{sortedDates.reduce((sum, date) => sum + (data.orphaned_logs?.[date]?.total_count || 0), 0)}</td>
                                <td className="px-4 py-2 border text-center bg-red-100">{sortedDates.reduce((sum, date) => sum + (data.deleted_bookings?.[date]?.total_count || 0), 0)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Legend/Notes */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm space-y-1">
                    <p>* Folder Total shows the sum of chats across all folders for each day</p>
                    <p>* Unique Bookings shows the actual count of distinct bookings for each day</p>
                    <p className="text-amber-600">* Orphaned Logs are audit entries where the original booking no longer exists</p>
                    <p className="text-red-600">* Deleted Bookings are soft-deleted bookings that still have audit logs</p>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
                <h1>User Activity</h1>

                <Formik
                    initialValues={{
                        audit_type: "chats_sent",
                        property_id: 0, // 0 means all properties
                        sort_by: "date", // user or date
                        include_bulk_messages: false, // exclude bulk messages by default
                    }}
                    onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
                        if (!fromDate) {
                            setErrors({ submit: 'Please select a From Date' });
                            setSubmitting(false);
                            return;
                        }

                        const formattedFromDate = moment(fromDate).format("YYYY-MM-DD");
                        const formattedToDate = toDate ? moment(toDate).format("YYYY-MM-DD") : null;
                        setLoading(true);

                        await axios({
                            method: "post",
                            url: `${process.env.REACT_APP_API_URL}reports/get_user_activity`,
                            data: {
                                audit_type: values.audit_type,
                                fromDate: formattedFromDate,
                                toDate: formattedToDate,
                                property_id: values.property_id,
                                sort_by: values.sort_by,
                                include_bulk_messages: values.include_bulk_messages,
                            },
                        })
                            .then((response) => {
                                setAuditLogs(response.data);
                            })
                            .catch((error) => {
                                console.log(error);
                            })
                            .finally(() => {
                                setLoading(false);
                                setSubmitting(false);
                            });
                    }}
                >
                    {({ values, errors, setFieldValue, handleSubmit, isValid, isSubmitting, dirty }) => (
                        <Form onSubmit={handleSubmit}>
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-5 mb-5">
                                    <div>
                                        <SettingsSelect
                                            label="Property"
                                            name="property_id"
                                            onChange={(name, value) => setFieldValue(name, value, true)}
                                            value={values.property_id}
                                            error={errors.property_id}
                                            options={properties}
                                        />
                                    </div>
                                    <div>
                                        <SettingsSelect
                                            label="Report Type"
                                            name="audit_type"
                                            onChange={(name, value) => {
                                                setFieldValue(name, value);
                                                setAuditLogs([]);
                                            }}
                                            value={values.audit_type}
                                            error={errors.audit_type}
                                            options={[
                                                { value: "chats_sent_month", label: "Chats Sent by Month" },
                                                { value: "chats_sent_day", label: "Chats Sent by Day" },
                                                { value: "bookings_updated", label: "Bookings Updated" },
                                                { value: "new_chats_day", label: "New Chats by Day" },
                                                { value: "chats_daily_folder", label: "Chats by Folder Daily" },
                                            ]}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">From Date</label>
                                        <DatePicker
                                            selected={fromDate}
                                            onChange={(value) => {
                                                setFromDate(value);
                                            }}
                                            className="z-20 block w-full font-light border border-gray-300 shadow-sm mt-1 py-2 pl-2 rounded-lg focus:outline-none focus:ring-primary focus:border-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                            dateFormat={"d MMMM yyyy"}
                                            peekNextMonth
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">To Date</label>
                                        <DatePicker
                                            selected={toDate}
                                            onChange={(value) => {
                                                setToDate(value);
                                            }}
                                            className="z-20 block w-full font-light border border-gray-300 shadow-sm mt-1 py-2 pl-2 rounded-lg focus:outline-none focus:ring-primary focus:border-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                            dateFormat={"d MMMM yyyy"}
                                            peekNextMonth
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                        />
                                    </div>
                                </div>

                                {(values.audit_type === "chats_sent_month" || values.audit_type === "chats_sent_day") && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                                        <div>
                                            <SettingsSelect
                                                label="Sort By"
                                                name="sort_by"
                                                onChange={(name, value) => setFieldValue(name, value, true)}
                                                value={values.sort_by}
                                                error={errors.sort_by}
                                                options={[
                                                    { value: "user", label: "User" },
                                                    { value: "date", label: "Date" },
                                                ]}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Bulk Messages</label>
                                            <label className="flex items-center cursor-pointer mt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={values.include_bulk_messages}
                                                    onChange={(e) => setFieldValue('include_bulk_messages', e.target.checked)}
                                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">Include Bulk Messages</span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <SubmitButton name="Submit" disabled={!isValid || isSubmitting || !fromDate} isSubmitting={isSubmitting} text="View Data" />
                            </div>

                            <div className="mt-5">
                                {loading === true ? <p>Loading...</p> : <>{values.audit_type === "chats_daily_folder" ? renderFolderTable(auditLogs) : renderRegularTable(values, auditLogs)}</>}
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* Staff Messages Detail Modal */}
            {showStaffModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowStaffModal(false)}>
                    <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">{staffModalData.title}</h3>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleDownloadStaffMessages}
                                    className="inline-flex items-center px-4 py-2 rounded-lg border text-sm font-medium focus:z-10 focus:outline-none border-primary bg-primary text-white hover:bg-primary_hover focus:ring-primary focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out shadow-sm"
                                >
                                    Download Excel
                                </button>
                                <button onClick={() => setShowStaffModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
                                    ×
                                </button>
                            </div>
                        </div>

                        {loadingStaffModal ? (
                            <div className="text-center py-8">Loading...</div>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <input
                                        type="text"
                                        placeholder="Search messages..."
                                        value={modalSearchTerm}
                                        onChange={(e) => setModalSearchTerm(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-primary focus:border-primary"
                                    />
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50">
                                                <th className="text-left px-4 py-2 border">Booking Ref</th>
                                                <th className="text-left px-4 py-2 border">Platform</th>
                                                <th className="text-left px-4 py-2 border">Message</th>
                                                <th className="text-left px-4 py-2 border">Timestamp</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {staffModalData.messages.length > 0 ? (
                                                staffModalData.messages
                                                    .filter((msg) => {
                                                        if (!modalSearchTerm) return true;
                                                        const searchLower = modalSearchTerm.toLowerCase();
                                                        return (
                                                            (msg.booking_reference && msg.booking_reference.toLowerCase().includes(searchLower)) ||
                                                            (msg.message && msg.message.toLowerCase().includes(searchLower)) ||
                                                            (msg.platform && msg.platform.toLowerCase().includes(searchLower))
                                                        );
                                                    })
                                                    .map((msg, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-4 py-2 border">
                                                                {msg.booking_reference ? (
                                                                    <a
                                                                        href={`/booking/all?user_id=${msg.user_id}&booking_id=${msg.booking_id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-blue-600 hover:underline"
                                                                    >
                                                                        {msg.booking_reference}
                                                                    </a>
                                                                ) : (
                                                                    "-"
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 border text-center">
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                                                                    {msg.platform || "-"}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 border text-sm max-w-md">
                                                                <div className="whitespace-pre-wrap break-words">{msg.message || "-"}</div>
                                                            </td>
                                                            <td className="px-4 py-2 border whitespace-nowrap">{moment(msg.created_at).format("DD MMM YYYY HH:mm")}</td>
                                                        </tr>
                                                    ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="px-4 py-4 text-center text-gray-500">
                                                        No messages found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Calls Detail Modal */}
            {showCallsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCallsModal(false)}>
                    <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">{callsModalData.title}</h3>
                            <button onClick={() => setShowCallsModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
                                ×
                            </button>
                        </div>

                        {loadingCallsModal ? (
                            <div className="text-center py-8">Loading...</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="text-left px-4 py-2 border">Booking Ref</th>
                                            <th className="text-left px-4 py-2 border">Caller Name</th>
                                            <th className="text-left px-4 py-2 border">Caller Number</th>
                                            <th className="text-left px-4 py-2 border">Applicant Number</th>
                                            <th className="text-left px-4 py-2 border">Duration</th>
                                            <th className="text-left px-4 py-2 border">Status</th>
                                            <th className="text-left px-4 py-2 border">Timestamp</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {callsModalData.calls.length > 0 ? (
                                            callsModalData.calls.map((call, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2 border">
                                                        {call.booking_reference ? (
                                                            <a
                                                                href={`/booking/all?user_id=${call.user_id}&booking_id=${call.booking_id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:underline"
                                                            >
                                                                {call.booking_reference}
                                                            </a>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 border">{call.caller_name || "-"}</td>
                                                    <td className="px-4 py-2 border">{call.agent_number || "-"}</td>
                                                    <td className="px-4 py-2 border">{call.applicant_number || "-"}</td>
                                                    <td className="px-4 py-2 border text-center">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                            {call.formatted_duration}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2 border text-center">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                            call.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                            call.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                            call.status === 'busy' ? 'bg-yellow-100 text-yellow-800' :
                                                            call.status === 'no-answer' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {call.status || "-"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2 border whitespace-nowrap">{moment(call.created_at).format("DD MMM YYYY HH:mm")}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="px-4 py-4 text-center text-gray-500">
                                                    No calls found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
